import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load site settings:', error);
        // Use defaults if fetch fails
        setSettings({
          company_name: "JAMOH & CO AUTOS LTD",
          company_description: "Premium online car dealership and auto auction marketplace connecting buyers with quality vehicles worldwide.",
          office_address: "Plot 123, Victoria Island, Lagos, Nigeria",
          phone_number: "+234-800-JAMOH (52664)",
          primary_email: "info@jamohautos.com",
          secondary_email: "sales@jamohautos.com",
          whatsapp_url: "https://wa.me/2348001234567",
          facebook_url: "https://facebook.com/jamohautos",
          twitter_url: "https://twitter.com/jamohautos",
          instagram_url: "https://instagram.com/jamohautos",
          rc_number: "123456",
          license_text: "Licensed Auto Dealer",
          copyright_text: "2026 JAMOH & CO AUTOS LTD. All rights reserved."
        });
      }
    };
    fetchSettings();
  }, []);

  if (!settings) {
    return null; // or a loading skeleton
  }

  const handleWhatsAppClick = () => {
    window.open(settings.whatsapp_url, '_blank');
  };

  // Split company name for logo display
  const companyParts = settings.company_name.split(' ');
  const firstLetter = companyParts[0]?.[0] || 'J';

  return (
    <footer className="bg-surface border-t border-border mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
                <span className="text-2xl font-bold font-heading">{firstLetter}</span>
              </div>
              <div>
                <div className="text-lg font-heading font-bold tracking-wider">{companyParts.slice(0, -1).join(' ') || 'JAMOH & CO'}</div>
                <div className="text-xs text-textSecondary">{companyParts[companyParts.length - 1] || 'AUTOS LTD'}</div>
              </div>
            </div>
            <p className="text-textSecondary text-sm mb-4">
              {settings.company_description}
            </p>
            
            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsAppClick}
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white rounded-sm px-4 py-2 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Chat on WhatsApp</span>
            </button>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/marketplace" className="text-textSecondary hover:text-primary transition-colors">Browse Cars</Link></li>
              <li><Link to="/auctions" className="text-textSecondary hover:text-primary transition-colors">Live Auctions</Link></li>
              <li><Link to="/request-vehicle" className="text-textSecondary hover:text-primary transition-colors">Request a Car</Link></li>
              <li><Link to="/calculator" className="text-textSecondary hover:text-primary transition-colors">Import Calculator</Link></li>
              <li><Link to="/about" className="text-textSecondary hover:text-primary transition-colors">About Us</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3 text-textSecondary text-sm">
              <li className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{settings.office_address}</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>{settings.phone_number}</span>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href={`mailto:${settings.primary_email}`} className="hover:text-primary transition-colors">
                  {settings.primary_email}
                </a>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href={`mailto:${settings.secondary_email}`} className="hover:text-primary transition-colors">
                  {settings.secondary_email}
                </a>
              </li>
            </ul>
          </div>

          {/* Social & Legal */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">Follow Us</h3>
            <div className="flex space-x-3 mb-6">
              <a 
                href={settings.facebook_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-surfaceHighlight rounded-sm flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={settings.twitter_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-surfaceHighlight rounded-sm flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href={settings.instagram_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-surfaceHighlight rounded-sm flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>

            <div>
              <h4 className="font-heading font-bold text-sm mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/terms" className="text-textSecondary hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-textSecondary hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between text-textSecondary text-sm">
            <p>&copy; {settings.copyright_text}</p>
            <p className="mt-2 md:mt-0">RC Number: {settings.rc_number} | {settings.license_text}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;